import { kibelaContext } from "./kibelaContext";
import { logException } from "./log_exception";

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;

const nop = (_: string) => {};

const makeKey = (key) => `/__cache/${key}`;

let isCacheEnabled_: boolean | undefined = undefined;
async function isCacheEnabled() {
  if (isCacheEnabled_ !== undefined) {
    return isCacheEnabled_;
  }

  // On old browser or SSR, `window.caches` is not available.
  if (typeof window === "undefined" || !("caches" in window) || !kibelaContext.appVersion) {
    isCacheEnabled_ = false;
    return false;
  }

  try {
    // On Firefox's private browsing, `window.caches` exists but it throws an error.
    // So try to execute `window.caches.has` function.
    await window.caches.has("__dummy__");
    isCacheEnabled_ = true;
  } catch (e) {
    isCacheEnabled_ = false;
  }
  return isCacheEnabled_;
}

async function read(store: Cache, key: string) {
  const cacheKey = makeKey(key);
  const cached = await store.match(cacheKey);
  if (cached) {
    const { expiredAt, data } = await cached.json();
    if (Date.now() < expiredAt) {
      console.log(`[${key}] Cache hit`);
      return data;
    }
    console.log(`[${key}] Cache found but expired`);
  } else {
    console.log(`[${key}] Cache not found`);
  }

  return null;
}

export async function readCache<T>({ key }: { key: string }): Promise<T | null> {
  if (!(await isCacheEnabled())) {
    console.log(`[${key}] Cache feature is disabled`);
    return null;
  }

  const cacheStore = await window.caches.open(`kibelaCache-${kibelaContext.appVersion}`);
  return read(cacheStore, key);
}

export async function fetchCache<T>(
  { key, expiresIn }: { key: string; expiresIn: number },
  fallback: (updateCacheKey: (newCacheKey: string) => void) => Promise<T>,
): Promise<T> {
  if (!(await isCacheEnabled())) {
    console.log(`[${key}] Cache feature is disabled`);
    return fallback(nop);
  }

  try {
    const cacheStore = await window.caches.open(`kibelaCache-${kibelaContext.appVersion}`);

    const cachedData = await read(cacheStore, key);
    if (cachedData != null) {
      return cachedData;
    }

    let cacheKey = makeKey(key);
    const result = await fallback((newCacheKey) => {
      cacheKey = makeKey(newCacheKey);
    });
    const response = new Response(
      JSON.stringify({
        expiredAt: Date.now() + expiresIn,
        data: result,
      }),
    );
    cacheStore.put(cacheKey, response);
    return result;
  } catch (error) {
    if (error.name === "QuotaExceededError") {
      logException(error);
      return fallback(nop);
    }
    throw error;
  }
}

// Delete old app version's caches
setTimeout(async () => {
  if (await isCacheEnabled()) {
    const keys = await window.caches.keys();
    keys.forEach((key) => {
      if (key.startsWith("kibelaCache-") && !key.includes(kibelaContext.appVersion!)) {
        window.caches.delete(key);
      }
    });
  }
}, 3000);
